<template>
  <v-card>
    <v-card-title>
      <span class="text-subtitle-1">Hallazgos Twilio IA por plantel (diario)</span>
      <v-spacer></v-spacer>
      <v-btn
        color="green darken-1"
        dark
        small
        tile
        class="mr-2"
        :loading="generandoFaltantes"
        :disabled="!periodo"
        @click="generarFaltantes()"
      >
        <v-icon left small>mdi-whatsapp</v-icon>
        Generar faltantes
      </v-btn>
      <v-btn
        color="primary"
        small
        tile
        class="mr-2"
        :loading="cargando"
        @click="cargarReporte()"
      >
        <v-icon left small>mdi-refresh</v-icon>
        Actualizar
      </v-btn>
      <v-btn icon small @click="$emit('cerrar')"><v-icon>mdi-close</v-icon></v-btn>
    </v-card-title>

    <v-card-text>
      <v-row>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="fecha"
            type="date"
            label="Reporte del día"
            filled
            dense
            hide-details
            :max="hoy"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="5">
          <v-text-field
            v-model="search"
            label="Buscar plantel"
            filled
            dense
            hide-details
            append-icon="mdi-magnify"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="2">
          <v-select
            v-model="escuela"
            :items="escuelas"
            label="Escuela"
            filled
            dense
            hide-details
          ></v-select>
        </v-col>
      </v-row>

      <v-alert v-if="!periodo && !cargando" type="info" dense text class="mt-4 mb-0">
        Los domingos no hay reporte. El reporte del lunes incluye sábado y domingo.
      </v-alert>

      <template v-else-if="periodo">
        <v-alert type="info" dense text class="mt-4">
          Cómo le fue a cada plantel <b>{{ periodoTexto }}</b> en WhatsApp. Cuenta todo el personal del plantel (ventas, encargada y recepción);
          la IA revisa las 10 conversaciones con más interacción de cada plantel. Se genera solo de lunes a sábado a partir de las 5:00.
        </v-alert>

        <div v-if="plantelesFiltrados.length" class="mb-2">
          <v-chip small label class="mr-2 mb-1">{{ plantelesFiltrados.length }} planteles</v-chip>
          <v-chip small label class="mr-2 mb-1">{{ totalConversaciones }} conversaciones</v-chip>
          <v-chip v-if="pendientes" small label color="blue lighten-4" class="mr-2 mb-1">{{ pendientes }} en proceso</v-chip>
        </div>

        <v-data-table
          :headers="headers"
          :items="plantelesFiltrados"
          :search="search"
          :loading="cargando"
          item-key="id_plantel"
          show-expand
          :expanded.sync="expandidas"
          class="elevation-0"
          :mobile-breakpoint="100"
          dense
          :items-per-page="50"
        >
          <template v-slot:item.respuesta_promedio_min="{ item }">
            {{ item.estado === 'listo' ? formatoMinutosRetro( item.respuesta_promedio_min ) : '' }}
          </template>

          <template v-slot:item.pct_audios="{ item }">
            {{ item.estado === 'listo' && item.pct_audios !== null ? item.pct_audios + '%' : '' }}
          </template>

          <template v-slot:item.siguiente_paso="{ item }">
            <span v-if="item.estado === 'listo' && item.conversaciones_ia">{{ item.siguiente_paso }}/{{ item.conversaciones_ia }}</span>
          </template>

          <template v-slot:item.estado="{ item }">
            <div class="d-flex align-center">
              <v-chip v-if="item.estado === 'pendiente'" x-small color="grey" dark>En cola</v-chip>
              <v-chip v-else-if="item.estado === 'procesando'" x-small color="blue" dark>Analizando…</v-chip>
              <v-chip v-else-if="item.estado === 'error'" x-small color="red" dark>Error</v-chip>
              <v-chip v-else x-small color="green" dark>Listo</v-chip>
              <v-btn
                icon
                small
                color="green darken-1"
                class="ml-1"
                title="Regenerar este plantel"
                :disabled="item.estado === 'pendiente' || item.estado === 'procesando'"
                @click="regenerar( item )"
              >
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
            </div>
          </template>

          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="py-4">
              <v-simple-table v-if="item.personas_detalle.length" dense class="mb-4">
                <thead>
                  <tr>
                    <th>Persona</th>
                    <th>Puesto</th>
                    <th>Conversaciones</th>
                    <th>Mensajes</th>
                    <th>Audios</th>
                    <th>Resp. promedio</th>
                    <th>Sin responder</th>
                    <th>No contestaron 1er msg</th>
                    <th>Con PDF</th>
                    <th>Sin 2ª vuelta</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="persona in item.personas_detalle" :key="persona.id_usuario">
                    <td>{{ persona.nombre }}</td>
                    <td>{{ persona.puesto }}</td>
                    <td>{{ persona.conversaciones }}</td>
                    <td>{{ persona.mensajes }}</td>
                    <td>{{ persona.pct_audios !== null ? persona.pct_audios + '%' : '—' }}</td>
                    <td>{{ formatoMinutosRetro( persona.respuesta_promedio_min ) }}</td>
                    <td>{{ persona.sin_responder }}</td>
                    <td>{{ valorPersona( persona.no_contestaron_primer ) }}</td>
                    <td>{{ valorPersona( persona.contactos_pdf ) }}</td>
                    <td>{{ valorPersona( persona.sin_segunda_vuelta ) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>

              <HallazgosTwilioDetalle
                :hallazgos="item"
                :nombre="item.plantel"
                conversaciones-url="retroalimentacion.plantel.conversaciones"
                :conversaciones-params="{ id_plantel: item.id_plantel, fecha: fechaCargada }"
              />
            </td>
          </template>

          <template v-slot:no-data>
            <div class="py-4">
              Aún no se genera el reporte de este día. Usa "Generar faltantes" para crearlo.
            </div>
          </template>
        </v-data-table>
      </template>
    </v-card-text>

    <Alerta v-if="respuestaAlerta" :parametros="parametros"/>
  </v-card>
</template>

<script>
import Alerta from '@/components/alertas/Alerta.vue'
import validarErrores from '@/mixins/validarErrores'
import HallazgosTwilioDetalle from '@/components/retroalimentacion/HallazgosTwilioDetalle.vue'
import { formatoMinutosRetro } from '@/constants/retroalimentacion-comercial'

export default {
  name: 'ReporteHallazgosPlantel',

  mixins: [ validarErrores ],

  components: {
    Alerta,
    HallazgosTwilioDetalle,
  },

  data: () => ({
    fecha: '',
    fechaCargada: '',
    hoy: '',
    periodo: null,
    periodoTexto: '',
    planteles: [],
    cargando: false,
    generandoFaltantes: false,
    search: '',
    escuela: 0,
    escuelas: [
      { text: 'Todas', value: 0 },
      { text: 'INBI' , value: 1 },
      { text: 'FAST' , value: 2 },
    ],
    expandidas: [],
    timerEstado: null,
    headers: [
      { text: 'Plantel'        , value: 'plantel' },
      { text: 'Personas'       , value: 'personas' },
      { text: 'Conversaciones' , value: 'conversaciones' },
      { text: 'Resp. promedio' , value: 'respuesta_promedio_min' },
      { text: 'Audios'         , value: 'pct_audios' },
      { text: 'Sin responder'  , value: 'sin_responder' },
      { text: 'Sin 2ª vuelta'  , value: 'sin_segunda_vuelta' },
      { text: 'Siguiente paso' , value: 'siguiente_paso', sortable: false },
      { text: 'Estado'         , value: 'estado', sortable: false },
      { text: ''               , value: 'data-table-expand' },
    ],
  }),

  computed: {
    plantelesFiltrados () {
      return this.escuela ? this.planteles.filter(p => p.escuela === this.escuela) : this.planteles
    },

    totalConversaciones () {
      return this.plantelesFiltrados.reduce((a, p) => a + (p.conversaciones || 0), 0)
    },

    pendientes () {
      return this.planteles.filter(p => p.estado === 'pendiente' || p.estado === 'procesando').length
    },
  },

  watch: {
    fecha (valor, anterior) {
      if (anterior && valor) this.cargarReporte()
    },
  },

  created () {
    this.cargarReporte()
  },

  beforeDestroy () {
    clearTimeout(this.timerEstado)
  },

  methods: {
    formatoMinutosRetro,

    valorPersona (valor) {
      return valor === null || valor === undefined ? '—' : valor
    },

    cargarReporte (silencioso) {
      if (!silencioso) this.cargando = true
      const params = this.fecha ? { fecha: this.fecha } : {}
      return this.$http.get('retroalimentacion.plantel.reporte', { params }).then(response => {
        this.hoy = response.data.hoy
        this.fechaCargada = response.data.fecha_reporte
        if (!this.fecha) this.fecha = response.data.fecha_reporte
        this.periodo = response.data.periodo
        this.periodoTexto = response.data.periodo_texto
        this.planteles = response.data.planteles
        this.programarEstado()
      }).catch(error => {
        this.validarError(error)
      }).finally(() => { this.cargando = false })
    },

    programarEstado () {
      clearTimeout(this.timerEstado)
      if (!this.pendientes) return
      this.timerEstado = setTimeout(() => { this.cargarReporte(true) }, 10000)
    },

    generarFaltantes () {
      this.generandoFaltantes = true
      return this.$http.post('retroalimentacion.plantel.generar', { fecha: this.fechaCargada }).then(response => {
        if (!response.data.programados) {
          this.validarSuccess('Todos los planteles de este día ya tienen reporte')
        }
        return this.cargarReporte(true)
      }).catch(error => {
        this.validarError(error)
      }).finally(() => { this.generandoFaltantes = false })
    },

    regenerar (item) {
      return this.$http.post('retroalimentacion.plantel.generar', { fecha: this.fechaCargada, id_plantel: item.id_plantel }).then(() => {
        return this.cargarReporte(true)
      }).catch(error => {
        this.validarError(error)
      })
    },
  },
}
</script>
